import {Button, CloseButton, Dialog, Field, Flex, Icon, Input, Popover, Portal, Skeleton, Spinner, Text, VStack} from "@chakra-ui/react";
import {HiOutlinePlus, HiOutlineArrowUpTray} from "react-icons/hi2";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router";
import {useSearchParams} from "react-router-dom";
import ScenarioService from "@/services/agency/ScenarioService";
import UserService from "@/services/UserService";
import ClientService from "@/services/ClientService";
import {showToast} from "@/components/ui/toaster";
import ProductLineTypeSelect from "@/components/ui/selects/ProductLineTypeSelect";
import ConfirmDialog from "@/pages/clients/components/ConfirmDialog";
import {companyLabel} from "@/pages/clients/companyLabel";
import ScenariosToolbar from "@/pages/scenarios/components/ScenariosToolbar";
import ScenariosTable from "@/pages/scenarios/components/ScenariosTable";
import ScenariosPagination from "@/pages/scenarios/components/ScenariosPagination";
import {
    CHANGES_THAT_RESET_PAGE,
    DEFAULT_QUERY,
    hasActiveFilters,
    parseQuery,
    toApiParams,
    toSearchParams,
} from "@/pages/scenarios/scenariosQuery";

const EmptyState = ({title, hint, action}) => (
    <VStack w="full" py={16} gap={3}>
        <Text color="white" fontWeight="semibold">{title}</Text>
        {hint && <Text color="text.grey" fontSize="sm">{hint}</Text>}
        {action}
    </VStack>
);

const ScenariosPage = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const query = useMemo(() => parseQuery(searchParams), [searchParams]);

    const [data, setData] = useState({items: [], totalCount: 0, page: 1, pageSize: DEFAULT_QUERY.pageSize});
    const [status, setStatus] = useState("loading"); // loading | ready | error
    const reqId = useRef(0);

    const [responsibleUsers, setResponsibleUsers] = useState([]);
    const [responsibleUsersLoading, setResponsibleUsersLoading] = useState(true);
    const [clients, setClients] = useState([]);
    const [pendingResponsibleId, setPendingResponsibleId] = useState(null);

    // create scenario
    const [createOpen, setCreateOpen] = useState(false);
    const [newScenarioName, setNewScenarioName] = useState("");
    const [newScenarioType, setNewScenarioType] = useState(null);
    const [isCreating, setIsCreating] = useState(false);

    // import scenario
    const [importOpen, setImportOpen] = useState(false);
    const [importName, setImportName] = useState("");
    const [importType, setImportType] = useState(null);
    const [importFile, setImportFile] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const fileInputRef = useRef(null);

    // delete confirm
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [confirmBusy, setConfirmBusy] = useState(false);

    const load = useCallback(() => {
        const id = ++reqId.current;
        setStatus((s) => (s === "ready" ? "ready" : "loading"));
        ScenarioService.getList(toApiParams(query))
            .then((res) => {
                if (id !== reqId.current) return;
                setData({
                    items: res?.items ?? [],
                    totalCount: res?.totalCount ?? 0,
                    page: res?.page ?? query.page,
                    pageSize: res?.pageSize ?? query.pageSize,
                });
                setStatus("ready");
            })
            .catch(() => {
                if (id !== reqId.current) return;
                setStatus("error");
            });
    }, [query]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        UserService.getAll({pageSize: 200})
            .then((res) => setResponsibleUsers(res?.items ?? []))
            .catch(() => setResponsibleUsers([]))
            .finally(() => setResponsibleUsersLoading(false));

        ClientService.getCompanies({pageSize: 100})
            .then((res) => setClients(res?.items ?? []))
            .catch(() => setClients([]));
    }, []);

    const responsibleUserOptions = useMemo(
        () => responsibleUsers.map((u) => ({
            value: u.id,
            label: `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email || "Unknown user",
        })),
        [responsibleUsers],
    );

    const clientOptions = useMemo(
        () => clients.map((c) => ({value: c.id, label: companyLabel(c)})),
        [clients],
    );

    const patchQuery = (patch) => {
        const next = {...query, ...patch};
        if (Object.keys(patch).some((k) => CHANGES_THAT_RESET_PAGE.includes(k))) next.page = 1;
        setSearchParams(toSearchParams(next), {replace: true});
    };

    const handleSort = (field) => {
        if (query.sortBy === field) {
            patchQuery({sortDirection: query.sortDirection === "asc" ? "desc" : "asc"});
        } else {
            patchQuery({sortBy: field, sortDirection: field === "name" ? "asc" : "desc"});
        }
    };

    const clearFilters = () => setSearchParams(toSearchParams(DEFAULT_QUERY), {replace: true});

    const openScenario = (s) => navigate(`/scenarios/${s.id}`);

    const handleResponsibleChange = async (scenario, userId) => {
        const previous = scenario.responsibleUser ?? null;
        const nextUser = userId ? responsibleUsers.find((u) => u.id === userId) ?? null : null;
        setPendingResponsibleId(scenario.id);
        // optimistic
        setData((d) => ({
            ...d,
            items: d.items.map((it) => (it.id === scenario.id ? {...it, responsibleUser: nextUser} : it)),
        }));
        try {
            if (userId) await ScenarioService.setResponsibleUser(scenario.id, userId);
            else await ScenarioService.clearResponsibleUser(scenario.id);
            showToast("Saved", userId ? "Responsible person updated" : "Responsible person cleared", "success");
            load();
        } catch (err) {
            setData((d) => ({
                ...d,
                items: d.items.map((it) => (it.id === scenario.id ? {...it, responsibleUser: previous} : it)),
            }));
            showToast("Error", err?.response?.data?.error ?? "Could not update the responsible person", "error");
        } finally {
            setPendingResponsibleId(null);
        }
    };

    const handleDuplicate = (scenario) => {
        ScenarioService.duplicate(scenario.id)
            .then((res) => {
                showToast("Success", `Scenario "${scenario.name}" duplicated`, "success");
                if (res?.id) navigate(`/scenarios/${res.id}`);
                else load();
            })
            .catch(() => showToast("Error", "Failed to duplicate scenario", "error"));
    };

    const runDelete = async () => {
        if (!confirmDelete) return;
        setConfirmBusy(true);
        try {
            await ScenarioService.remove(confirmDelete.id);
            showToast("Deleted", `Scenario "${confirmDelete.name}" deleted`, "success");
            setConfirmDelete(null);
            load();
        } catch {
            showToast("Error", "Failed to delete scenario", "error");
        } finally {
            setConfirmBusy(false);
        }
    };

    const handleCreateScenario = () => {
        if (!newScenarioType) {
            showToast("Error", "Scenario type is required", "error");
            return;
        }
        setIsCreating(true);
        ScenarioService.create(newScenarioName, newScenarioType)
            .then((response) => navigate(`/scenarios/${response.id}`))
            .catch(() => showToast("Error", "Failed to create scenario", "error"))
            .finally(() => setIsCreating(false));
    };

    const handleImportDialogOpen = (open) => {
        setImportOpen(open);
        if (!open) {
            setImportName("");
            setImportType(null);
            setImportFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleImportScenario = () => {
        if (!importName.trim()) {
            showToast("Error", "Scenario name is required", "error");
            return;
        }
        if (!importType) {
            showToast("Error", "Scenario type is required", "error");
            return;
        }
        if (!importFile) {
            showToast("Error", "Please select a JSON file", "error");
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            let parsed;
            try {
                parsed = JSON.parse(e.target.result);
            } catch {
                showToast("Error", "Invalid JSON file", "error");
                return;
            }

            if (!Array.isArray(parsed.intents)) {
                showToast("Error", "Invalid scenario format: missing 'intents' array", "error");
                return;
            }

            setIsImporting(true);
            ScenarioService.importScenario(importName.trim(), importType, parsed.intents)
                .then((response) => {
                    showToast("Success", `Scenario "${importName}" imported successfully`, "success");
                    navigate(`/scenarios/${response.id}`);
                })
                .catch(() => {
                    showToast("Error", "Failed to import scenario. Check the file format and try again.", "error");
                })
                .finally(() => setIsImporting(false));
        };
        reader.readAsText(importFile);
    };

    const scenarios = data.items;
    const showSkeleton = status === "loading" && scenarios.length === 0;

    return (
        <VStack w="full" rowGap={5} align="stretch">
            <Flex flexDirection="row" justifyContent="space-between" alignItems="center" w="full" gap={3} flexWrap="wrap">
                <Text color="white" fontSize={24} fontWeight="semibold">Scenarios</Text>
                <Flex gap={3}>
                    <Button variant="outlined" textDecoration="unset" onClick={() => handleImportDialogOpen(true)}>
                        <Icon fontSize={20}><HiOutlineArrowUpTray/></Icon>
                        <Text>Import scenario</Text>
                    </Button>

                    <Popover.Root open={createOpen} onOpenChange={(e) => setCreateOpen(e.open)} positioning={{placement: "bottom-end"}}>
                        <Popover.Trigger asChild>
                            <Button variant="primary" textDecoration="unset">
                                <Icon fontSize={46}><HiOutlinePlus/></Icon>
                                <Text>Create scenario</Text>
                            </Button>
                        </Popover.Trigger>
                        <Portal>
                            <Popover.Positioner>
                                <Popover.Content w={96}>
                                    <Popover.Arrow/>
                                    <Popover.Body>
                                        <Field.Root required>
                                            <Field.Label color="white" fontSize={18}>
                                                Scenario name <Field.RequiredIndicator/>
                                            </Field.Label>
                                            <Input variant="primary" placeholder="Enter scenario name" mb={2} value={newScenarioName} onChange={(e) => setNewScenarioName(e.target.value)}/>
                                        </Field.Root>
                                        <ProductLineTypeSelect value={newScenarioType} onChange={setNewScenarioType}/>
                                        <Button variant="primary" textDecoration="unset" w="full" mt={2} onClick={handleCreateScenario} disabled={isCreating || !newScenarioName.trim() || !newScenarioType}>
                                            {isCreating ? <Spinner size="md" color="white"/> : (
                                                <>
                                                    <Icon fontSize={46}><HiOutlinePlus/></Icon>
                                                    <Text>Create</Text>
                                                </>
                                            )}
                                        </Button>
                                    </Popover.Body>
                                </Popover.Content>
                            </Popover.Positioner>
                        </Portal>
                    </Popover.Root>
                </Flex>
            </Flex>

            <ScenariosToolbar
                query={query}
                onChange={patchQuery}
                onClear={clearFilters}
                responsibleUsers={responsibleUserOptions}
                clients={clientOptions}
            />

            {showSkeleton ? (
                <VStack w="full" gap={2} align="stretch">
                    {Array.from({length: 8}).map((_, i) => (
                        <Skeleton key={i} height="52px" borderRadius={8}/>
                    ))}
                </VStack>
            ) : status === "error" ? (
                <EmptyState
                    title="Couldn’t load scenarios"
                    hint="Something went wrong while contacting the server."
                    action={<Button variant="outlined" onClick={load}>Retry</Button>}
                />
            ) : scenarios.length === 0 ? (
                hasActiveFilters(query) ? (
                    <EmptyState
                        title="No scenarios match your filters."
                        action={<Button variant="outlined" onClick={clearFilters}>Clear filters</Button>}
                    />
                ) : (
                    <EmptyState
                        title="No scenarios yet."
                        action={
                            <Button variant="primary" onClick={() => setCreateOpen(true)}>
                                <Icon fontSize={46}><HiOutlinePlus/></Icon>
                                <Text>Create scenario</Text>
                            </Button>
                        }
                    />
                )
            ) : (
                <>
                    <ScenariosTable
                        scenarios={scenarios}
                        query={query}
                        startIndex={(data.page - 1) * data.pageSize}
                        onSort={handleSort}
                        onOpen={openScenario}
                        onDuplicate={handleDuplicate}
                        onDelete={setConfirmDelete}
                        responsibleUsers={responsibleUsers}
                        responsibleUsersLoading={responsibleUsersLoading}
                        onResponsibleChange={handleResponsibleChange}
                        pendingResponsibleId={pendingResponsibleId}
                        dimmed={status === "loading"}
                    />
                    <ScenariosPagination
                        page={data.page}
                        pageSize={data.pageSize}
                        totalCount={data.totalCount}
                        onPageChange={(p) => patchQuery({page: p})}
                        onPageSizeChange={(size) => patchQuery({pageSize: size})}
                    />
                </>
            )}

            <Dialog.Root lazyMount open={importOpen} onOpenChange={(e) => handleImportDialogOpen(e.open)} placement="center">
                <Portal>
                    <Dialog.Backdrop/>
                    <Dialog.Positioner>
                        <Dialog.Content bg="bg.card" borderWidth={1} borderColor="border.secondary" borderRadius={12} p={2} minW={96}>
                            <Dialog.Header>
                                <Dialog.Title color="white">Import scenario</Dialog.Title>
                                <Dialog.CloseTrigger asChild>
                                    <CloseButton color="white" size="sm"/>
                                </Dialog.CloseTrigger>
                            </Dialog.Header>
                            <Dialog.Body>
                                <VStack gap={4} alignItems="flex-start">
                                    <Field.Root required w="full">
                                        <Field.Label color="white">
                                            Scenario name <Field.RequiredIndicator/>
                                        </Field.Label>
                                        <Input
                                            variant="primary"
                                            placeholder="Enter new scenario name"
                                            value={importName}
                                            onChange={(e) => setImportName(e.target.value)}
                                        />
                                    </Field.Root>
                                    <ProductLineTypeSelect value={importType} onChange={setImportType}/>
                                    <Field.Root required w="full">
                                        <Field.Label color="white">
                                            JSON file <Field.RequiredIndicator/>
                                        </Field.Label>
                                        <Input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".json"
                                            color="white"
                                            sx={{'::file-selector-button': {display: 'none'}}}
                                            onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
                                        />
                                    </Field.Root>
                                </VStack>
                            </Dialog.Body>
                            <Dialog.Footer>
                                <Button variant="outlined" onClick={() => handleImportDialogOpen(false)} disabled={isImporting}>
                                    Cancel
                                </Button>
                                <Button variant="primary" onClick={handleImportScenario} disabled={isImporting || !importName.trim() || !importType || !importFile}>
                                    {isImporting ? <Spinner size="sm" color="white"/> : "Import"}
                                </Button>
                            </Dialog.Footer>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>

            <ConfirmDialog
                open={!!confirmDelete}
                title="Delete scenario"
                body={confirmDelete ? `Delete scenario "${confirmDelete.name}"? This also deletes its intents and cannot be undone.` : ""}
                confirmLabel="Delete"
                isLoading={confirmBusy}
                onCancel={() => !confirmBusy && setConfirmDelete(null)}
                onConfirm={runDelete}
            />
        </VStack>
    );
};

export default ScenariosPage;
